
import React, { useMemo } from 'react';
import type { BlockRendererProps } from '@/lib/email-builder/block-registry';
import { parseBlockConfiguration } from '@/types/email-blocks';
import type { GroupBlockConfiguration } from '@/types/block-configurations';
import { DragGhostPlaceholder } from '../DragGhostPlaceholder';
import { BlockRenderer } from '../BlockRenderer';
import { useDragState } from '../DndProvider';

// mj-group accepts only top/middle/bottom; map the flex-style config values
const ITEMS_ALIGN_MAP: Record<string, React.CSSProperties['alignItems']> = {
  stretch: 'stretch',
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
};

export function GroupBlock({ block, isSelected, isHovered, onSelect, onHover }: BlockRendererProps) {
  const config = parseBlockConfiguration(block) as GroupBlockConfiguration;

  const sortedChildBlocks = useMemo(() => {
    return [...(block.childBlocks || [])].sort((a, b) => a.orderIndex - b.orderIndex);
  }, [block.childBlocks]);

  const hasChildren = sortedChildBlocks.length > 0;
  const { dropTarget, activeData } = useDragState();

  return (
    <div
      onClick={(e) => { e.stopPropagation(); onSelect(block.id); }}
      onMouseEnter={(e) => { e.stopPropagation(); onHover(block.id); }}
      onMouseLeave={() => onHover(null)}
      className={`
        lw-block-group cursor-pointer rounded transition-all
        ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}
        ${isHovered && !isSelected ? 'ring-1 ring-muted-foreground/50' : ''}
      `}
      style={{
        // Mirrors mj-group: background + direction only — no padding, border, or gap
        backgroundColor: config.background?.color || 'transparent',
        direction: (config.direction || 'ltr') as React.CSSProperties['direction'],
      }}
    >
      {hasChildren ? (
        // Groups hold columns side by side (mj-group is always horizontal)
        <div
          className="flex flex-wrap w-full"
          style={{ alignItems: ITEMS_ALIGN_MAP[config.itemsAlign] || 'stretch' }}
        >
          {sortedChildBlocks.map((childBlock, idx) => (
            <React.Fragment key={childBlock.id}>
              {dropTarget?.parentId === block.id && dropTarget.index === idx && (
                <DragGhostPlaceholder orientation="vertical" />
              )}
              <BlockRenderer block={childBlock} parentId={block.id} index={idx} depth={1} />
            </React.Fragment>
          ))}
          {dropTarget?.parentId === block.id && dropTarget.index === sortedChildBlocks.length && (
            <DragGhostPlaceholder orientation="vertical" />
          )}
        </div>
      ) : (
        <div className={`rounded-lg border-2 border-dashed flex items-center justify-center min-h-16 ${
          activeData ? 'border-primary/40 bg-primary/5' : 'border-border/60'
        }`}>
          {dropTarget?.parentId === block.id ? (
            <DragGhostPlaceholder />
          ) : (
            <p className={`text-sm font-medium select-none ${
              activeData ? 'text-primary/60' : 'text-muted-foreground/50'
            }`}>
              Drop columns here — they stay side by side on mobile
            </p>
          )}
        </div>
      )}
    </div>
  );
}
